import type { ApiException } from '@/common/error';
import type { StatusTone } from '@/components/StatusPill/StatusPill';
import { isOwnerGroupRole, OWNER_GROUP_ROLES, UserRole } from '@/utils/roles';
import { calendarDayToLocalDate } from '@/utils/timezone';

import type {
  AwayLeaveDto,
  LeaveDto,
  LeaveEventConflictDto,
  LeaveStatusDto,
  LeaveTypeDto,
} from './leaves.dto';

export type LeaveType = LeaveTypeDto;
export type LeaveStatus = LeaveStatusDto;

export interface LeaveEventConflict {
  eventId: string;
  title: string;
  start?: string;
  end?: string;
  allDay: boolean;
}

export interface Leave {
  id: string;
  personId: string;
  /** Only on the list, as the people service names them. */
  personName?: string;
  /** Only on the list: their role on the vessel, absent once they have left it. */
  personRole?: string;
  type: LeaveType;
  status: LeaveStatus;
  /** The vessel's calendar day, `YYYY-MM-DD`. */
  startDate: string;
  /** Absent while a sick leave is open. */
  endDate?: string;
  note?: string;
  declineReason?: string;
  createdBy: string;
  decidedBy?: string;
  createdAt: string;
  conflicts: LeaveEventConflict[];
}

export const mapLeaveConflict = (dto: LeaveEventConflictDto): LeaveEventConflict => ({
  eventId: dto.event_id,
  title: dto.title,
  start: dto.start,
  end: dto.end,
  allDay: dto.all_day,
});

export const mapLeave = (dto: LeaveDto): Leave => ({
  id: dto.id,
  personId: dto.person_id,
  personName: dto.person_name,
  personRole: dto.person_role,
  type: dto.type,
  status: dto.status,
  startDate: dto.start_date,
  endDate: dto.end_date,
  note: dto.note,
  declineReason: dto.decline_reason,
  createdBy: dto.created_by,
  decidedBy: dto.decided_by,
  createdAt: dto.created_at,
  conflicts: (dto.conflicts ?? []).map(mapLeaveConflict),
});

/** Someone away on an approved leave, as an event's planner sees it. */
export interface AwayLeave {
  personId: string;
  startDate: string;
  endDate?: string;
}

export const mapAwayLeave = (dto: AwayLeaveDto): AwayLeave => ({
  personId: dto.person_id,
  startDate: dto.start_date,
  endDate: dto.end_date,
});

/** `2 event assignments overlap this leave.`, or nothing when none do. */
export const leaveConflictWarning = (conflicts: LeaveEventConflict[]): string | undefined =>
  conflicts.length > 0
    ? `${conflicts.length} event ${conflicts.length === 1 ? 'assignment overlaps' : 'assignments overlap'} this leave.`
    : undefined;

export const LEAVE_TYPE_LABEL: Record<LeaveType, string> = {
  vacation: 'Vacation',
  sick_leave: 'Leave',
};

/**
 * A leave day as people read it, `Jul 18, 2026` (voice guide D7b). The value is already the
 * vessel's calendar day, so it is drawn as that day wherever the browser is.
 */
export const formatLeaveDay = (day: string): string =>
  calendarDayToLocalDate(day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

/** `Nov 3, 2026 – Nov 8, 2026`, or `From Nov 3, 2026` for a sick leave still open. */
export const formatLeaveDays = (leave: Pick<Leave, 'startDate' | 'endDate'>): string =>
  leave.endDate
    ? `${formatLeaveDay(leave.startDate)} – ${formatLeaveDay(leave.endDate)}`
    : `From ${formatLeaveDay(leave.startDate)}`;

/**
 * `On leave: Apr 9 – Apr 11, 2027`, the year said once when both days share it, or
 * `On leave from Apr 9, 2027` while the end is open.
 */
export const formatAwayLabel = (leave: Pick<Leave, 'startDate' | 'endDate'>): string => {
  if (!leave.endDate) return `On leave from ${formatLeaveDay(leave.startDate)}`;
  if (leave.startDate.slice(0, 4) !== leave.endDate.slice(0, 4)) {
    return `On leave: ${formatLeaveDays(leave)}`;
  }
  const first = calendarDayToLocalDate(leave.startDate).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  return `On leave: ${first} – ${formatLeaveDay(leave.endDate)}`;
};

/** Under way: started on or before `today`, and not over. ISO days sort as strings. */
export const leaveStarted = (leave: Leave, today: string): boolean => leave.startDate <= today;

export const leaveOver = (leave: Leave, today: string): boolean =>
  !!leave.endDate && leave.endDate < today;

/**
 * Where a leave stands. An approved leave says whether it is ahead, happening now or done,
 * because that is what the reader wants to know about it. The pill and the leave table's
 * status cut both read it, so the cut is named in the pill's own words.
 */
export type LeaveDisplayStatus =
  | 'pending'
  | 'approved'
  | 'on_leave'
  | 'ended'
  | 'declined'
  | 'cancelled';

export const leaveDisplayStatus = (leave: Leave, today: string): LeaveDisplayStatus => {
  // Ended by Back from Leave, or over by its own last day: either way it is behind them.
  if (leave.status !== 'approved') return leave.status;
  if (leaveOver(leave, today)) return 'ended';
  return leaveStarted(leave, today) ? 'on_leave' : 'approved';
};

export const LEAVE_STATUS_PILL: Record<LeaveDisplayStatus, { label: string; tone: StatusTone }> = {
  pending: { label: 'Awaiting Approval', tone: 'warning' },
  approved: { label: 'Approved', tone: 'success' },
  on_leave: { label: 'On Leave', tone: 'progress' },
  ended: { label: 'Ended', tone: 'neutral' },
  declined: { label: 'Declined', tone: 'danger' },
  cancelled: { label: 'Canceled', tone: 'neutral' },
};

export const leaveStatusPill = (leave: Leave, today: string) =>
  LEAVE_STATUS_PILL[leaveDisplayStatus(leave, today)];

/** How many days a leave runs, both ends counted; none for a sick leave still open. */
export const leaveDayCount = (leave: Pick<Leave, 'startDate' | 'endDate'>): number | undefined =>
  leave.endDate
    ? Math.round(
        (Date.parse(`${leave.endDate}T00:00:00Z`) - Date.parse(`${leave.startDate}T00:00:00Z`)) /
          86_400_000,
      ) + 1
    : undefined;

/**
 * Who decides on leave: the owner group and captains. A role, not a right, as the people service
 * reads it: whatever rights an owner hands out or takes away, these are who run the vessel.
 */
export const LEAVE_MANAGER_ROLES: readonly UserRole[] = [...OWNER_GROUP_ROLES, UserRole.CAPTAIN];

const runsTheVessel = (role?: string | null): boolean =>
  isOwnerGroupRole(role) || role === UserRole.CAPTAIN;

/** Who looks at a leave, and whose leave it is: the people service decides on the same facts. */
export interface LeaveViewer {
  viewerId?: string;
  viewerRole?: string | null;
  holderRole?: string | null;
  /** The viewer is the captain running an ownerless vessel as its owner. */
  actingCaptain?: boolean;
}

/**
 * Whether the viewer approves, declines and manages this person's leave: the owner group or a
 * captain for the crew, only the owner group, or the captain acting as owner, for another captain
 * or one of themselves. The owner group and the captain decide their own: it still waits as a
 * request until they approve it.
 */
export const decidesLeaveOf = (
  holderId: string,
  { viewerId, viewerRole, holderRole, actingCaptain }: LeaveViewer,
): boolean =>
  holderId === viewerId
    ? runsTheVessel(viewerRole)
    : runsTheVessel(viewerRole) &&
      (!runsTheVessel(holderRole) || isOwnerGroupRole(viewerRole) || !!actingCaptain);

export const decidesLeave = (leave: Leave, viewer: LeaveViewer): boolean =>
  decidesLeaveOf(leave.personId, viewer);

/**
 * What the viewer may do with a leave, mirroring the people service so a button is never
 * offered for a write it would refuse.
 */
export const leaveActions = (leave: Leave, viewer: LeaveViewer, today: string) => {
  const self = leave.personId === viewer.viewerId;
  const decides = decidesLeave(leave, viewer);
  const open = leave.status === 'pending' || leave.status === 'approved';
  const started = leaveStarted(leave, today);
  const over = leaveOver(leave, today);
  const vacation = leave.type === 'vacation';
  const pendingVacation = vacation && leave.status === 'pending';

  return {
    // A vacation under way only moves its end, and only by a manager.
    edit: open && (vacation && started ? decides : self || decides),
    cancel: open && !over && (decides || (self && vacation && !started)),
    // A vacation runs to the day agreed: only a manager ends one early.
    close: leave.status === 'approved' && started && !over && (decides || (self && !vacation)),
    approve: pendingVacation && decides && !over,
    decline: pendingVacation && decides,
    // Erasing is for clearing mistakes, so it is open whatever state the leave is in.
    remove: decides,
  };
};

const LEAVE_ERRORS: Record<string, string> = {
  LEAVE_OVERLAP: 'These days overlap another leave. Please pick other days.',
  VACATION_IN_PAST: 'A vacation can’t start before today. Please pick a later day.',
  SICK_LEAVE_IN_FUTURE: 'Leave starts today or earlier. Please request a vacation instead.',
  LEAVE_END_REQUIRED: 'Please pick the last day of the vacation.',
  INVALID_LEAVE_DATES: 'The last day can’t be before the first. Please pick the days again.',
  LEAVE_STARTED: 'This vacation has started. Please ask the captain to change it.',
  NOT_LEAVE_MANAGER: 'Only the owner or the captain can change this leave.',
  LEAVES_FORBIDDEN: 'Only the vessel’s crew can take leave.',
  LEAVE_TARGET_NOT_CREW: 'Only the vessel’s crew can take leave.',
  LEAVE_CLOSED: 'This leave has been declined, canceled or ended. Please reload the page.',
  LEAVE_NOT_PENDING: 'This request has already been decided. Please reload the page.',
  LEAVE_OVER: 'This leave is already over.',
  LEAVE_NOT_ACTIVE: 'This leave isn’t under way. Please reload the page.',
  SICK_LEAVE_NOT_CANCELLABLE: 'Please use Back from Leave to end a leave.',
  VACATION_AGREED:
    'A vacation runs to its agreed last day. Please ask the captain to end it early.',
  LEAVE_NOT_FOUND: 'This leave is no longer on the vessel. Please reload the page.',
};

/** Another leave a LEAVE_OVERLAP ran into, as the people service names it in `data.overlaps`. */
interface LeaveOverlapDto {
  id: string;
  type: LeaveTypeDto;
  status: LeaveStatusDto;
  start_date: string;
  end_date: string;
}

const OVERLAP_STATUS: Partial<Record<LeaveStatusDto, string>> = {
  pending: 'awaiting approval',
  approved: 'approved',
  ended: 'ended',
};

/** `Vacation, Nov 2, 2026 – Nov 5, 2026 (approved)`, for each leave the days ran into. */
const overlapsMessage = (overlaps: LeaveOverlapDto[]): string =>
  `These days overlap ${overlaps.length === 1 ? 'another leave' : 'other leaves'}: ${overlaps
    .map((o) => {
      const status = OVERLAP_STATUS[o.status];
      return `${LEAVE_TYPE_LABEL[o.type]}, ${formatLeaveDays({ startDate: o.start_date, endDate: o.end_date })}${status ? ` (${status})` : ''}`;
    })
    .join('; ')}.`;

/** The rule a leave write ran into, or `fallback` for anything else. */
export const leaveErrorMessage = (error: unknown, fallback: string): string => {
  const exception = error as Partial<ApiException> | null;
  const type = exception?.type;
  const overlaps = (exception?.data as { overlaps?: LeaveOverlapDto[] } | undefined)?.overlaps;
  if (type === 'LEAVE_OVERLAP' && overlaps?.length) return overlapsMessage(overlaps);
  return (type && LEAVE_ERRORS[type]) || fallback;
};
